import React from 'react'

import { format } from 'date-fns'
import { KeyRoundIcon, MessageSquareIcon, Trash2Icon, TriangleAlertIcon } from 'lucide-react'

import { Box, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material'

import { Button } from '@repo/ui'

import type { AuthFactor } from 'hooks/api/useAuthFactors'

import { factorWarningBadge } from './authFactorStatus'

interface AuthFactorRowProps {
  factor: AuthFactor
  onRemove: (factor: AuthFactor) => void
  /**
   * Make this the factor a sign-in asks for first. Omitted when the user has only one
   * usable factor, where there is nothing to choose between.
   */
  onMakePreferred?: (factor: AuthFactor) => void
  isChangingPreferred?: boolean
}

/**
 * One row in the enrolled-factors list. `Trash2Icon` (not `X`) per
 * working-conventions §38 — removing a factor deletes the
 * `user_auth_factors` row, so it's a real delete and gets the confirm modal
 * (`RemoveFactorModal`) this row's click opens, not an inline removal.
 */
export const AuthFactorRow = ({ factor, onRemove, onMakePreferred, isChangingPreferred }: AuthFactorRowProps) => {
  const theme = useTheme()
  const isTotp = factor.type === 'totp'
  // SMS opt-out: the number replied STOP, so sign-in codes cannot be texted to it until the
  // person texts START (the API clears the flag on the next text that goes through).
  const warningBadge = factorWarningBadge(factor)

  // VRG-822 — the METHOD names the row, always. This used to be
  // `factor.label || <method>`, and `auth_factors.label` has no UI anywhere that
  // writes it, so the only values in that column are hand-written ones. A
  // break-glass SMS factor seeded with label "Break-glass" therefore rendered as
  // "Break-glass" where every other row renders its method, which reads as a
  // method the product does not have.
  //
  // The column is NOT dropped: TOTP enrollment already accepts a label
  // (start-totp-enrollment), so if an enrollment UI ever lets someone name a
  // device, that name belongs in the SUBLABEL beside "Ending in 86" — a
  // qualifier on the method, never a replacement for it.
  const label = isTotp ? 'Authenticator app' : 'SMS'
  // Never render the full phone number back to the user — last two digits
  // only, per this task's explicit constraint. `phoneLastFour` from the API
  // is actually the last FOUR — trim to two here rather than widen the
  // constraint on the API response shape.
  const sublabel = isTotp
    ? factor.verified
      ? `Added ${format(new Date(factor.createdAt), 'MMM d, yyyy')}`
      : 'Setup incomplete'
    : factor.phoneLastFour
      ? `Ending in ${factor.phoneLastFour.slice(-2)}`
      // VRG-822 — was 'Text message', which restated the label. Now that the label
      // IS the method, a sublabel naming the method again says nothing; this rare
      // branch (an SMS factor whose digits the API did not return) says what is
      // actually true instead.
      : 'Number unavailable'

  return (
    // VRG-490 round 2 — matches the row padding the house uses for the same shape
    // (icon circle + two-line text + trailing action) in a divider-separated
    // settings list, rather than inventing a new value for this row.
    //
    // VRG-611 — py: 4 (16px each side on the 4px base), raised from py: 2 on 2026-09-14.
    // At 8px these rows measured 56px against the Login card's 113px on the same
    // page, and read as compressed beside it. Login has no row padding at all — its
    // air comes from a parent Stack spacing={4} plus a 40px action button — so
    // matching it meant raising the rows that DO use padding.
    // Moved across all three surfaces together (this, AuthFactorRow, PaymentMethods)
    // rather than one, because the previous value was a deliberate three-surface
    // convention and changing one would trade a page-level inconsistency for an
    // app-level one.
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={3}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.primary[50] || theme.palette.grey[100],
            color: theme.palette.text.primary,
            flexShrink: 0,
          }}
        >
          {isTotp ? (
            <KeyRoundIcon size={18} strokeWidth={1.5} />
          ) : (
            <MessageSquareIcon size={18} strokeWidth={1.5} />
          )}
        </Box>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={2} alignItems="baseline">
            <Typography variant="body2">{label}</Typography>
            {/* Plain secondary text, matching "This device" on the sessions list below. */}
            {factor.verified && factor.preferred && onMakePreferred && (
              <Typography variant="body3" color="text.secondary">
                Asked first at sign-in
              </Typography>
            )}
            {warningBadge && (
              <Tooltip title="Reply START to any message from Vera to turn texts back on. Until then, sign in with another method or a recovery code.">
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<TriangleAlertIcon size={14} strokeWidth={1.5} />}
                  label={warningBadge}
                  data-testid="sms-opted-out-badge"
                  sx={{ alignSelf: 'center' }}
                />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="body3" color="text.secondary">
            {factor.allowed === false ? 'Not allowed for your role · remove it or add another method' : sublabel}
            {factor.allowed !== false &&
              factor.lastUsedAt &&
              ` · Last used ${format(new Date(factor.lastUsedAt), 'MMM d, yyyy')}`}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2}>
        {/* §46 — record state HIDES: "Make primary" is absent on the factor that already is
            primary, on an unverified or disallowed one, and when there is only one usable factor. */}
        {onMakePreferred && factor.verified && factor.allowed !== false && !factor.preferred && (
          <Button variant="tertiary" disabled={isChangingPreferred} onClick={() => onMakePreferred(factor)}>
            Make primary
          </Button>
        )}

        <Tooltip title={`Remove ${label.toLowerCase()}`}>
          <span>
            <IconButton
              size="small"
              onClick={() => onRemove(factor)}
              aria-label={`Remove ${label}`}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': { color: theme.palette.error.main },
              }}
            >
              <Trash2Icon strokeWidth={1} size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  )
}
